import { z } from 'zod';

const quotationSourceSchema = z.object({
  quotationId: z.string().min(1),
  version: z.number().int().positive(),
  customerName: z.string().min(1),
  projectName: z.string().min(1),
  localeMode: z.enum(['thai', 'english', 'bilingual']),
  tiers: z.array(
    z.object({
      quantity: z.number().int().positive(),
      unitPrice: z.string(),
      currency: z.string().length(3),
    }),
  ),
  validityDate: z.string(),
  assumptions: z.array(z.string()),
  exclusions: z.array(z.string()),
  internal: z.object({
    formulaPercentages: z.record(z.string(), z.string()),
    supplierPrices: z.record(z.string(), z.string()),
    margin: z.string(),
    regulatoryNotes: z.string(),
  }),
});

export type QuotationDocumentSource = z.input<typeof quotationSourceSchema>;

export interface CustomerProposalPayload {
  readonly quotationId: string;
  readonly quotationVersion: number;
  readonly customerName: string;
  readonly projectName: string;
  readonly localeMode: 'thai' | 'english' | 'bilingual';
  readonly tiers: readonly {
    readonly quantity: number;
    readonly unitPrice: string;
    readonly currency: string;
  }[];
  readonly validityDate: string;
  readonly assumptions: readonly string[];
  readonly exclusions: readonly string[];
}

export function buildCustomerProposalPayload(
  source: QuotationDocumentSource,
): CustomerProposalPayload {
  const parsed = quotationSourceSchema.parse(source);
  return {
    quotationId: parsed.quotationId,
    quotationVersion: parsed.version,
    customerName: parsed.customerName,
    projectName: parsed.projectName,
    localeMode: parsed.localeMode,
    tiers: parsed.tiers,
    validityDate: parsed.validityDate,
    assumptions: parsed.assumptions,
    exclusions: parsed.exclusions,
  };
}

const dossierDirectories = [
  '00-index',
  '01-company',
  '02-facility',
  '03-product',
  '04-formula',
  '05-ingredients',
  '06-process',
  '07-specifications',
  '08-tests',
  '09-label',
  '10-claims',
  '11-authorizations',
  '12-submission-history',
] as const;

export interface DossierFile {
  readonly path: string;
  readonly sha256: string;
  readonly size: number;
}

export function buildDossierManifest(files: readonly DossierFile[]) {
  return {
    version: 1 as const,
    directories: [...dossierDirectories],
    files: [...files].sort((left, right) => left.path.localeCompare(right.path)),
  };
}

export * from './oem';
